import _ from "lodash";
import { DateTime } from "luxon";

import parse from "@/utils/parse";

const DATE_PROPERTIES = ["birthdate", "birthDate", "moveInDate", "move-inDate"];

class User {
	// CASL subject type detection
	static get modelName() {
		return "User";
	}

	constructor(user) {

		const {
			_id,
			email,
			_secondaryEmail,
			systems,
			communities,
			groups = []
		} = user;
		this.guid = _id;
		this._email = {
			primary: email,
			alternate: _secondaryEmail,
		};
		this.associations = groups;
		this.properties = user;
		this.systems = systems;
		this.communities = communities;
	}

	getProperty(name) {
		const val = _.trim(_.get(this.properties, name));
		if (_.isEmpty(val)) return null;

		// Special treatment for a few known property names
		if (_.includes(DATE_PROPERTIES, name)) {
			return parse.dateTime(val);
		}

		// Treat everything else like a string
		return val;
	}

	get firstName() {
		return this.getProperty("firstName");
	}

	get lastName() {
		return this.getProperty("lastName");
	}

	get profileUrl() {
		return this.getProperty("photo");
	}

	get birthday() {
		return this.getProperty("birthdate") || this.getProperty("birthDate");
	}

	get readableBirthday() {
		return this.birthday
			? this.birthday.toLocaleString(DateTime.DATE_FULL)
			: null;
	}

	get moveInDate() {
		// Both seem to be used in different places...
		return this.getProperty("moveInDate") || this.getProperty("move-inDate");
	}

	get address() {
		return {
			address: this.getProperty("address"),
			zipcode: this.getProperty("zip"),
		};
	}

	get initials() {
		const first = _.get(this, "firstName.0", "");
		const last = _.get(this, "lastName.0", "");
		const initials = `${first}${last}`;
		return _.isEmpty(initials) ? null : initials;
	}

	get displayName() {
	

		const name = _.trim(`${this.firstName || ""} ${this.lastName || ""}`);
		if (!_.isEmpty(name)) return name;

		return null;
	}

	get email() {
		return this._email.primary || this._email.alternate || null;
	}

	get phone() {
		return (
			this.getProperty("primaryPhone") ||
			this.getProperty("phone") ||
			this.getProperty("roomPhone")
		);
	}

	get careSetting() {
		return _.toLower(this.getProperty("careSetting"));
	}

	get isSystemAdmin() {
		return this.properties._roles.indexOf('6eaaaf20-2b5e-57ab-858a-46edd7cf55e7') > -1;
	}

	get relationship() {
		return this.getProperty("family.relationship") || (this.properties._type === 'family') ? 'Other' : null;
	}

	// Returns either 'Admin' or 'Member'
	get userType() {
		if (this.isSystemAdmin) return "Admin";
		return "Member";
	}

	get lifecircles() {
		return _.filter(this.associations, { type: "lifecircle" });
	}

	// Returns true if user name, email, or phone number matches provided filter string
	filter(term) {
		term = _.toLower(term);
		return _.some(["displayName", "email", "phone"], key => {
			const val = _.toLower(this[key]);
			if (!_.isNil(val)) return val.indexOf(term) !== -1;
		});
	}
}

export function createUser(item) {
	return Object.freeze(new User(item));
}
